import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import axios from '../services/managedAxios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SiteContext = createContext(null);

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const { isAuthenticated, canAccessSite, user } = useAuth();
  const canSites = !!(user?.is_owner || user?.is_super_admin || user?.legacy_access || user?.permission_keys?.includes('masters.site.view'));
  const canProjects = !!(user?.is_owner || user?.is_super_admin || user?.legacy_access || user?.permission_keys?.includes('masters.project.view'));
  const [sites, setSites] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load sites when authenticated
  useEffect(() => {
    const loadSites = async () => {
      if (isAuthenticated) {
        setLoading(true);
        try {
          const [sitesRes, projectsRes] = await Promise.all([
            canSites ? axios.get(`${API}/sites`) : Promise.resolve({ data: { sites: [] } }),
            canProjects ? axios.get(`${API}/projects`) : Promise.resolve({ data: { projects: [] } })
          ]);
          setSites(sitesRes.data.sites || []);
          setProjects(projectsRes.data.projects || []);
          
          // Restore selected site from storage
          const savedSiteId = localStorage.getItem('selectedSiteId');
          if (savedSiteId) {
            const site = sitesRes.data.sites.find(s => s.id === savedSiteId);
            if (site && canAccessSite(site.id)) {
              setSelectedSite(site);
            }
          }
        } catch (error) {
          console.error('Failed to load sites:', error);
        }
        setLoading(false);
      }
    };
    loadSites();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, canSites, canProjects]);

  const selectSite = (site) => {
    if (site && canAccessSite(site.id)) {
      setSelectedSite(site);
      localStorage.setItem('selectedSiteId', site.id);
    }
  };

  const clearSite = () => {
    setSelectedSite(null);
    localStorage.removeItem('selectedSiteId');
  };

  const refreshSites = async () => {
    try {
      const response = await axios.get(`${API}/sites`);
      setSites(response.data.sites || []);
    } catch (error) {
      console.error('Failed to refresh sites:', error);
    }
  };

  const getOfficeSite = () => {
    return sites.find(s => s.is_office);
  };

  const getSiteById = (siteId) => {
    return sites.find(s => s.id === siteId);
  };

  const getProjectById = (projectId) => {
    return projects.find(p => p.id === projectId);
  };

  const value = {
    sites,
    projects,
    selectedSite,
    loading,
    selectSite,
    clearSite,
    refreshSites,
    getOfficeSite,
    getSiteById,
    getProjectById,
    hasSiteSelected: !!selectedSite
  };

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
};

export default SiteContext;
